/**
 * `slider` renderer — composes `@nextday/funnel-ui` `SliderStep` (single
 * slider per screen) or `SliderGroup` (multiple sliders on one screen),
 * dispatched on `data.mode`.
 *
 * Step JSON shape (single — plan default):
 *
 *   {
 *     "type": "slider",
 *     "data": {
 *       "mode": "single",
 *       "question": "What's your age?",
 *       "min": 18, "max": 65, "step": 1,
 *       "initialValue": 28,
 *       "unit": " yrs",
 *       "answerKey": "age"
 *     }
 *   }
 *
 * Step JSON shape (group — plan §5 step 6):
 *
 *   {
 *     "type": "slider",
 *     "data": {
 *       "mode": "group",
 *       "question": "Fine-tune the personality.",
 *       "answerKey": "personality_sliders",
 *       "touchedAnswerKey": "slider_touched",
 *       "sliders": [
 *         {
 *           "id": "directness",
 *           "leftLabel": "Gentle", "rightLabel": "Direct",
 *           "defaultValue": 40,
 *           "summaryBands": [
 *             { "max": 34,  "label": "Slightly gentle" },
 *             { "max": 64,  "label": "Balanced" },
 *             { "max": 100, "label": "A little direct" }
 *           ]
 *         }, ...
 *       ]
 *     }
 *   }
 *
 * `summaryBands` is JSON-authorable (admins / agent edit copy without code);
 * the renderer turns it into a `formatSummary` callback at runtime.
 */
import { SliderGroup, SliderStep, type SliderGroupItem } from "@nextday/funnel-ui";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";

const summaryBandSchema = z.object({
  /** Inclusive upper bound for this band. Bands are evaluated in order. */
  max: z.number().int(),
  label: z.string().min(1),
});

const groupSliderSchema = z.object({
  id: z.string().min(1),
  leftLabel: z.string().min(1),
  rightLabel: z.string().min(1),
  defaultValue: z.number(),
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().optional(),
  summaryBands: z.array(summaryBandSchema).min(1).optional(),
});

const singleSchema = z
  .object({
    mode: z.literal("single"),
    question: z.string().min(1),
    description: z.string().optional(),
    answerKey: z.string().min(1),
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().optional(),
    initialValue: z.number().optional(),
    unit: z.string().optional(),
  })
  .strict();

const groupSchema = z
  .object({
    mode: z.literal("group"),
    question: z.string().min(1),
    description: z.string().optional(),
    answerKey: z.string().min(1),
    touchedAnswerKey: z.string().min(1).optional(),
    sliders: z.array(groupSliderSchema).min(1),
  })
  .strict();

export const sliderSchema = z.discriminatedUnion("mode", [singleSchema, groupSchema]);

export type SliderData = z.infer<typeof sliderSchema>;
export type SliderSingleData = z.infer<typeof singleSchema>;
export type SliderGroupData = z.infer<typeof groupSchema>;

const buildFormatSummary = (
  bands: ReadonlyArray<z.infer<typeof summaryBandSchema>> | undefined,
): ((value: number) => string) | undefined => {
  if (!bands || bands.length === 0) return undefined;
  return (value) => {
    for (const band of bands) {
      if (value <= band.max) return band.label;
    }
    return bands[bands.length - 1]?.label ?? "";
  };
};

const buildGroupItems = (sliders: SliderGroupData["sliders"]): SliderGroupItem[] =>
  sliders.map((slider) => {
    const item: SliderGroupItem = {
      id: slider.id,
      leftLabel: slider.leftLabel,
      rightLabel: slider.rightLabel,
      defaultValue: slider.defaultValue,
      ...(slider.min !== undefined ? { min: slider.min } : {}),
      ...(slider.max !== undefined ? { max: slider.max } : {}),
      ...(slider.step !== undefined ? { step: slider.step } : {}),
    };
    const formatSummary = buildFormatSummary(slider.summaryBands);
    if (formatSummary) item.formatSummary = formatSummary;
    return item;
  });

const SliderRenderer = ({ data, step, answers, onAnswer }: RendererProps<SliderData>) => {
  if (data.mode === "single") {
    const initial =
      typeof answers[data.answerKey] === "number"
        ? (answers[data.answerKey] as number)
        : data.initialValue;

    return (
      <SliderStep
        question={data.question}
        description={data.description}
        min={data.min}
        max={data.max}
        step={data.step}
        initialValue={initial}
        unit={data.unit}
        onChange={(value) => onAnswer(data.answerKey, { value })}
        testId={`renderer-slider-single-${step.id}`}
      />
    );
  }

  const initialValues =
    typeof answers[data.answerKey] === "object" &&
    answers[data.answerKey] !== null &&
    !Array.isArray(answers[data.answerKey])
      ? (answers[data.answerKey] as Record<string, number>)
      : undefined;

  const items = buildGroupItems(data.sliders);

  return (
    <SliderGroup
      question={data.question}
      description={data.description}
      sliders={items}
      initialValues={initialValues}
      onChange={(values) => onAnswer(data.answerKey, { value: values })}
      onTouch={
        data.touchedAnswerKey
          ? () => onAnswer(data.touchedAnswerKey as string, { value: true })
          : undefined
      }
      testId={`renderer-slider-group-${step.id}`}
    />
  );
};

export const sliderRenderer = createRenderer({
  type: "slider",
  dataSchema: sliderSchema,
  label: "Slider",
  Component: SliderRenderer,
});

export default sliderRenderer;
